import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { Button } from "@shared/ui/Button";
import { Alert, Avatar, Flex, Rate, Space } from "antd";
import { useEffect } from "react";

import styles from "./styles.module.scss";

import { ToastService } from "@shared/lib/services";
import {
  IBriefInfluencerV2,
  IInfluencerCostRequest,
} from "@shared/lib/types/briefInfluencerV2";

import { useAddInfluencerCost } from "@entities/brief/lib/hooks/useAddInfluencerCost";
import { useAddInfluencerInitialCost } from "@entities/brief/lib/hooks/useAddInfluencerInitialCost";
import { AddCommercials } from "./AddCommercials";
import { Deliverables } from "./Deliverables";

import { useEditInfluencerCost } from "@entities/brief/lib/hooks/useEditInfluencerCost";
import { getInfoDetails } from "@entities/influencer/lib/utils/getInfoDetails";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";

interface InfluencerCommercialProps {
  briefInfluencer: IBriefInfluencerV2;
  onClose: () => void;
  type: "view" | "create-new" | "create";
}

export function InfluencerCommercial({
  briefInfluencer,
  onClose,
  type,
}: InfluencerCommercialProps) {
  const isViewOnly = type === "view";

  const { briefId, brief } = useBriefStore();

  const creatorInfo = getInfoDetails(briefInfluencer);

  const { addInitialCost, isAdded, isAdding } =
    useAddInfluencerInitialCost(briefId);
  const {
    addCost,
    isAdded: isAddedCost,
    isAdding: isAddingCost,
  } = useAddInfluencerCost(briefId);

  const { editCost, isEditing, isEdited } = useEditInfluencerCost(briefId);

  const handleSaveCommercials = (
    values: Omit<IInfluencerCostRequest, "briefInfluencerId">
  ) => {
    if (isViewOnly) {
      return editCost({
        ...values,
        briefInfluencerId: briefInfluencer._id,
      });
    }
    if (type === "create-new") {
      addCost({
        ...values,
        briefInfluencerId: briefInfluencer._id,
      });
    } else {
      addInitialCost({
        ...values,
        briefInfluencerId: briefInfluencer._id,
      });
    }
  };

  useEffect(() => {
    if (isAdded) {
      ToastService.success("Initial cost added successfully");
      onClose();
    }
    if (isAddedCost) {
      ToastService.success("Cost added successfully");
      onClose();
    }
    if (isEdited) {
      ToastService.success("Cost updated successfully");
      onClose();
    }
  }, [isAdded, isAddedCost, isEdited]);

  const isPricingPocAssigned = Boolean(
    briefInfluencer.assignedPricingPocV2?.assignedToUserId
  );

  return (
    <Flex vertical gap={24}>
      {!isPricingPocAssigned ? (
        <Alert
          message="Pricing POC not assigned. Please assign a Pricing POC to continue."
          type="warning"
          showIcon
        />
      ) : null}
      <div className={styles.infoCard}>
        <Flex justify="space-between">
          <div className={styles.profileSection}>
            <Avatar src={creatorInfo.dp} className={styles.avatar} size={72} />
            <div>
              <Space align="end">
                <div className={styles.name}>{creatorInfo.name}</div>
                {creatorInfo?.isVerified && (
                  <img src="/instaTick.svg" alt="verified" />
                )}
              </Space>

              <Button
                type="link"
                size="small"
                style={{ display: "block", padding: "0" }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(creatorInfo?.link, "_blank");
                }}
              >
                <Space style={{ color: "#888", fontSize: 12 }} size={2}>
                  {getPlatformIcon(creatorInfo?.platform || "")}
                  {creatorInfo?.username}
                </Space>
              </Button>

              <div className={styles.rating}>
                <Rate disabled value={creatorInfo.rating} />
              </div>
            </div>
          </div>
          {/* <div className={styles.metaSection}>
            <div>
              <span className={styles.typeTag}>
                {creatorInfo.type} {creatorInfo?.agency && " : "}
                {creatorInfo?.agency}
              </span>
            </div>
            <div>
              <b>Phone:</b> {creatorInfo.phone}
            </div>
            <div>
              <b>Email:</b> {creatorInfo.email}
            </div>
            <div>
              <b>Location:</b> {creatorInfo.location}
            </div>
          </div> */}
        </Flex>
      </div>

      <div className={styles.infoCard} style={{ padding: 20 }}>
        <Deliverables
          deliverable={briefInfluencer?.deliverableV2}
          briefId={briefId}
          brief={brief}
        />
      </div>
      <div className={styles.infoCard} style={{ padding: 20 }}>
        <AddCommercials
          isViewOnly={isViewOnly}
          briefInfluencer={briefInfluencer}
          initialValues={{
            cost: briefInfluencer.influencerCost?.cost,
            note: briefInfluencer?.influencerCost?.note,
          }}
          onClose={onClose}
          isLoading={isAdding || isAddingCost || isEditing}
          handleSave={handleSaveCommercials}
        />
      </div>
    </Flex>
  );
}
